import React, { useState, useCallback, useMemo, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { login, googleAuth } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';

import styles from './styles/Auth.module.css';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { setIsAuthenticated } = useAuth();

    const isFormValid = useMemo(() => {
        return formData.email && formData.password;
    }, [formData]);

    const handleSubmit = useCallback(async e => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await login(formData);
            setIsAuthenticated(true);
            const from = location.state?.from;
            const isAuthPage = from?.pathname === '/login' || from?.pathname === '/register';
            const redirectPath = !from || isAuthPage
                ? '/chats'
                : `${from.pathname || ''}${from.search || ''}${from.hash || ''}`;
            navigate(redirectPath, { replace: true });
        } catch (error) {
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [formData, location.state, navigate, setIsAuthenticated]);

    const handleGoogleSuccess = useCallback(async tokenResponse => {
        setError('');
        setIsLoading(true);

        try {
            // Get user profile with the access token
            const profileResponse = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo?access_token=${tokenResponse.access_token}`);
            const profileData = await profileResponse.json();

            // Use the ID token from the profile data
            if (profileData.sub) {
                await googleAuth(tokenResponse.access_token);
                setIsAuthenticated(true);
                const from = location.state?.from;
                const isAuthPage = from?.pathname === '/login' || from?.pathname === '/register';
                const redirectPath = !from || isAuthPage
                    ? '/chats'
                    : `${from.pathname || ''}${from.search || ''}${from.hash || ''}`;
                navigate(redirectPath, { replace: true });
            } else {
                setError('Failed to get Google profile');
            }
        } catch (error) {
            setError(error.message || 'Google authentication failed');
        } finally {
            setIsLoading(false);
        }
    }, [location.state, navigate, setIsAuthenticated]);

    const handleGoogleError = useCallback(() => {
        setError('Google sign-in failed. Please try again.');
    }, []);

    const loginButton = useGoogleLogin({
        onSuccess: tokenResponse => handleGoogleSuccess(tokenResponse),
        onError: error => handleGoogleError(error),
        scope: 'email profile',
    });

    const handleChange = useCallback(e => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    }, []);

    return (
        <div className={styles.container}>
            <Link to="/">
                <img 
                    src="/icons/android-icon-192x192.png" 
                    alt="JetChats Logo" 
                    className={styles.logo}
                />
            </Link>
            <div className={styles.card}>
                <h2>Log in to your account</h2>

                <button onClick={loginButton} className={styles.googleButton}>
                    <span>Continue with Google</span>
                </button>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email address"
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="password">Password</label>
                        <div className={styles.passwordInput}>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••••"
                                required
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isLoading || !isFormValid}
                    >
                        {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                    {error && <div className={styles.error}>{error}</div>}
                    <p className={styles.signInText}>
                        Don't have an account? <Link to="/register" state={location.state}>Sign up</Link>
                    </p>
                    <div className={styles.termsText}>
                        <p>
                            By using JetChats you agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default memo(Login);
